import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Gem, Loader2, MapPin, Music, Shirt, Stamp, Upload, UserCircle2, Users, Waves } from 'lucide-react';
import { Button } from '../../ui/button';
import { absUrl, errMsg, uploadAsset } from '../../../lib/aiStudioApi';
import { fmtBytes } from '../../../lib/shrinkImage';
import { cn } from '../../../lib/utils';

export const FOLDER_ICON = { actors: Users, characters: UserCircle2, locations: MapPin, wardrobe: Shirt, props: Gem, music: Music, sfx: Waves, logos: Stamp };
export const isAudioFolder = (f) => f === 'music' || f === 'sfx';

/** Upload one or many files into a folder — images are shrunk in the browser first (≤1600 px). */
export function AssetUploadButton({ folder, onUploaded, label = 'Upload', size = 'sm', className, testId = 'asset-upload-btn' }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState('');
  const audio = isAudioFolder(folder);
  async function upload(files) {
    let saved = 0, before = 0, after = 0;
    for (const [i, f] of files.entries()) {
      setBusy(`${i + 1}/${files.length}`);
      try { const a = await uploadAsset(f, folder); saved += 1; before += a.original_size || a.size; after += a.size; onUploaded?.(a); }
      catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
    }
    setBusy('');
    if (saved) toast.success(`${saved} file${saved > 1 ? 's' : ''} saved${!audio && before > after ? ` · shrunk ${fmtBytes(before)} → ${fmtBytes(after)}` : ''}`);
  }
  return (
    <>
      <input ref={ref} type="file" multiple accept={audio ? '.mp3,.wav,.m4a,.aac,.ogg,audio/*' : 'image/jpeg,image/png,image/webp,image/gif'} className="hidden"
        data-testid={`${testId}-input`} onChange={(e) => { const fs = [...(e.target.files || [])]; e.target.value = ''; if (fs.length) upload(fs); }} />
      <Button type="button" size={size} disabled={!!busy} onClick={() => ref.current?.click()} data-testid={testId}
        className={cn('bg-amber-500 text-neutral-900 hover:bg-amber-400', className)}>
        {busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Upload size={13} className="mr-1" />}{busy ? `Uploading ${busy}…` : label}
      </Button>
    </>
  );
}

/** One asset — image thumbnail or playable audio, with its size (and how much it was shrunk). */
export function AssetTile({ asset, selected, onClick, children, testId }) {
  const Icon = FOLDER_ICON[asset.folder] || Gem;
  const shrunk = asset.original_size > asset.size * 1.05;
  return (
    <div className={cn('group overflow-hidden rounded-xl border bg-neutral-900/50 transition-colors duration-200',
      selected ? 'border-amber-400' : 'border-neutral-800 hover:border-neutral-600')} data-testid={testId}>
      <button type="button" onClick={onClick} disabled={!onClick} className="relative block aspect-square w-full overflow-hidden bg-neutral-950">
        {asset.media === 'image'
          ? <img src={absUrl(asset.url)} alt={asset.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          : <span className="flex h-full w-full flex-col items-center justify-center gap-2 text-neutral-500"><Icon size={26} /><span className="text-[10px] uppercase tracking-wider">{asset.folder === 'music' ? 'Music' : 'Sound effect'}</span></span>}
        {asset.source === 'generated' && <span className="absolute left-2 top-2 rounded bg-amber-500 px-1.5 py-0.5 text-[9px] font-semibold text-neutral-900">AI cast lock</span>}
      </button>
      <div className="space-y-1.5 p-2.5">
        <div className="truncate text-xs font-medium text-neutral-100" title={asset.name}>{asset.name}</div>
        {asset.media === 'audio' && <audio src={absUrl(asset.url)} controls preload="none" className="h-7 w-full" data-testid={testId ? `${testId}-audio` : undefined} />}
        <div className="flex flex-wrap gap-1 text-[10px] text-neutral-400">
          {asset.size > 0 && <span className="rounded-full bg-neutral-800 px-1.5 py-0.5">{fmtBytes(asset.size)}{shrunk ? ` · was ${fmtBytes(asset.original_size)}` : ''}</span>}
          {asset.width > 0 && <span className="rounded-full bg-neutral-800 px-1.5 py-0.5">{asset.width}×{asset.height}</span>}
          {asset.project_title && <span className="truncate rounded-full bg-neutral-800 px-1.5 py-0.5">{asset.project_title}</span>}
        </div>
        {children}
      </div>
    </div>
  );
}
